// src/components/events/EventForm.js

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const EventForm = ({
  eventData,
  setEventData,
  handleSubmit,
  handleCancel,
  submitting,
  isNotice,
  isEdit, // New prop
}) => {
  // Function to get the current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const tzoffset = new Date().getTimezoneOffset() * 60000; // Offset in milliseconds
    return new Date(Date.now() - tzoffset).toISOString().split('T')[0];
  };

  // Update event data when isNotice changes, only if not editing
  useEffect(() => {
    if (!isEdit) { // Only set dates if not editing
      if (isNotice) {
        const currentDate = getCurrentDate();
        setEventData((prev) => ({
          ...prev,
          startDate: currentDate,
          endDate: currentDate,
        }));
      } else {
        setEventData((prev) => ({
          ...prev,
          startDate: '',
          endDate: '',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotice, isEdit]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Event Name */}
        <Grid item xs={12}>
          <TextField
            label="Title"
            name="title"
            variant="outlined"
            fullWidth
            value={eventData.title}
            onChange={(e) =>
              setEventData((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
            required
          />
        </Grid>

        {/* Conditionally Render Date Fields */}
        {!isNotice ? (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                name="startDate"
                type="date"
                variant="outlined"
                fullWidth
                value={eventData.startDate}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                name="endDate"
                type="date"
                variant="outlined"
                fullWidth
                value={eventData.endDate}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              This is a notice. Start and end dates are automatically set to the current date.
            </Typography>
          </Grid>
        )}

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            label="Description"
            name="description"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={eventData.description}
            onChange={(e) =>
              setEventData((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
          />
        </Grid>

        {/* Conditionally Render Location, Organizer, Contact Info */}
        {!isNotice && (
          <>
            {/* Location */}
            <Grid item xs={12}>
              <TextField
                label="Location"
                name="location"
                variant="outlined"
                fullWidth
                value={eventData.location}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.value,
                  }))
                }
              />
            </Grid>

            {/* Organizer */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Organizer"
                name="organizer"
                variant="outlined"
                fullWidth
                value={eventData.organizer}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.value,
                  }))
                }
              />
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Info"
                name="contactInfo"
                variant="outlined"
                fullWidth
                value={eventData.contactInfo}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.value,
                  }))
                }
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Submit and Reset Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<CloseIcon />}
          onClick={handleCancel}
          sx={{ mr: 2, textTransform: 'none', fontWeight: 'bold' }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={
            !eventData.title ||
            (!isNotice && (!eventData.startDate || !eventData.endDate)) ||
            submitting
          }
          sx={{ textTransform: 'none', fontWeight: 'bold' }}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Save'
          )}
        </Button>
      </Box>
    </Box>
  );
};

EventForm.propTypes = {
  eventData: PropTypes.shape({
    eventId: PropTypes.number,
    title: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.string,
    organizer: PropTypes.string,
    contactInfo: PropTypes.string,
  }).isRequired,
  setEventData: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  isNotice: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired, // New prop
};

export default EventForm;